import { AnimateWinsComponent } from "ags/components/animate-wins-component";
import { BigWinThresholds, winMagnitude } from "ags/utils/win-magnitude";
import { CloseCommand } from "appworks/commands/comms/close-command";
import { Components } from "appworks/components/components";
import { gameState } from "appworks/model/game-state";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { SuperState } from "appworks/state-machine/states/superstate";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { wrapperImplementation } from "gameiom/wrapper/wrapper-implementation";
import Logger = require("js-logger");
import { SignalBinding } from "signals";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { BonusResult } from "slotworks/model/gameplay/records/results/bonus-result";
import { AutoplayService } from "slotworks/services/autoplay/autoplay-service";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";

export class AgsRollUpState extends SuperState {
    protected rollupHasCompleted: boolean;
    protected cashWon: number;
    protected spinWon: number;
    protected collapseState: Function;
    protected signalBindings: SignalBinding[] = [];
    protected consideredWin: number;
    protected inFreespins: boolean = false;
    protected pendingFreespins: boolean = false;
    protected hasBonusWin: boolean = false;
    protected collapsing: boolean;
    protected waitingForClose: boolean;

    public onEnter(): void {
        this.collapsing = false;
        this.waitingForClose = false;
        this.rollupHasCompleted = false;

        uiFlags.set(UIFlag.AWAITING_RESPONSE, false);

        this.cashWon = gameState.getCurrentGame().getTotalWin();
        this.spinWon = gameState.getCurrentGame().getCurrentRecord().wonAmount;
        this.inFreespins = gameState.getCurrentGame().getFreespinsPlayed() > 0;
        this.hasBonusWin = gameState.getCurrentGame().getCurrentRecord().getResultsOfType(BonusResult).length > 0;
        this.pendingFreespins = gameState.getCurrentGame().areFreespinsPending();
        this.consideredWin = this.getConsideredWin();

        this.signalBindings.push(Services.get(SlotBetService).onBetChange.add(() => this.collapse()));

        Components.get(AnimateWinsComponent).startAnimating(this.consideredWin);

        this.executeRollup().then(() => {
            this.onRollupComplete();
        });
    }

    public onSubStateSkip(): void {
        Services.get(SoundService).customEvent("cancel_rollup");
        uiFlags.set(UIFlag.GAME_IN_PROGRESS, true);

        if (this.rollupHasCompleted) {
            if (Services.get(AutoplayService).isAutoPlaying()) {
                this.confirmComplete();
            } else {
                this.confirmSkip();
            }
            return;
        }

        if (!this.waitingForClose) {
            Services.get(TransactionService).setWinnings(this.cashWon).execute();
            Components.get(AnimateWinsComponent).endBigWinAnimation(true).execute();

            this.closeGame().then(() => this.onRollupComplete());
        }
    }

    public onRollupComplete(): void {
        this.setBalance();
        this.rollupHasCompleted = true;
    }

    public onSubStateComplete(): void {
        if (this.rollupHasCompleted) {
            this.confirmComplete();
        } else {
            this.repeat();
        }
    }

    public isRollupComplete(): boolean {
        return this.rollupHasCompleted;
    }

    public onExit(): void {
        Components.get(AnimateWinsComponent).end();
        Services.get(SoundService).customEvent("end_rollup");

        if (!this.collapsing) {
            uiFlags.set(UIFlag.IDLE, false);
            uiFlags.closeMenus();
        }

        this.setBalance();

        for (const binding of this.signalBindings) {
            binding.detach();
        }

        this.signalBindings = [];
    }

    public collapse(): void {
        this.collapsing = true;
        Services.get(SoundService).customEvent("end_rollup");
        Components.get(AnimateWinsComponent).endBigWinAnimation(true).execute();
        Services.get(TransactionService).setTotalWin(0);

        super.collapse();
    }

    protected setBalance(): void {
        if (!this.inFreespins) {
            Services.get(TransactionService).setBalance(gameState.getCurrentGame().balance);
        }
    }

    protected playRollupAnimations(duration: number): Contract<void> {
        return new Contract((resolve) => {

            Services.get(TransactionService).setWinnings(this.cashWon, this.cashWon, duration).then(() => {
                Components.get(AnimateWinsComponent).endBigWinAnimation().execute();
                resolve(null);
            });
        });
    }

    protected getRollUpAudioContract(
        winRatio: number,
        shouldPlayBonusSound: boolean,
        isHugeWin: boolean
    ): () => Contract<void> {
        if (shouldPlayBonusSound) {
            Services.get(SoundService).customEvent("ringup1");
            return Contract.empty;
        }

        if (winRatio === 0) {
            return Contract.empty;
        }

        const bigWinSound = winMagnitude.getSoundAlias(winRatio);

        if (isHugeWin) {
            return Contract.empty;
        } else {
            Services.get(SoundService).customEvent(bigWinSound);
            return Contract.empty;
        }
    }

    protected getConsideredWin(): number {
        const currentRecord = gameState.getCurrentGame().getCurrentRecord();

        let consideredWin = this.cashWon;

        if (this.inFreespins) {
            consideredWin = this.spinWon;
        }

        if (this.hasBonusWin) {
            consideredWin = this.spinWon + gameState.getCurrentGame().getTotalChildrenWin(currentRecord);
        }

        return consideredWin;
    }

    protected executeRollup(): Contract<void> {
        const winRatio = this.consideredWin / Services.get(SlotBetService).getTotalStake();
        const isBigWin = winRatio >= BigWinThresholds.BIG;
        const shouldPlayBonusSound = this.isFreespinRecord() && !isBigWin;
        const duration = gameState.getCurrentGame().inFreespins() ? 5000 : winMagnitude.getSoundDuration(winRatio, shouldPlayBonusSound)
        Logger.log(`Playing roll up for win level ${winMagnitude.getWinLevel(winRatio)} (win ratio ${winRatio})`);

        const soundComplete = this.getRollUpAudioContract(winRatio, shouldPlayBonusSound, isBigWin);

        const methods: Array<() => Contract<void>> = [
            () => new Parallel<any>([
                () => this.playRollupAnimations(duration),
                () => soundComplete()
            ])
        ];

        methods.push(() => Contract.wrap(() => Services.get(SoundService).customEvent("end_rollup")));

        // since we are only here if we have winnings, this will only happen on a normal spin which has winnings
        methods.push(() => this.closeGame());
        uiFlags.set(UIFlag.GAME_IN_PROGRESS, false);

        return new Sequence<any>(methods);
    }

    protected isFreespinRecord() {
        const currentGame = gameState.getCurrentGame();
        return currentGame.isCurrentRecordType(FreespinRecord);
    }

    protected closeGame(): Contract<void> {
        this.waitingForClose = true;

        return new Contract<void>((resolve) => {
            let closeContract: Contract<void>;

            if (!this.inFreespins && !this.hasBonusWin) {
                closeContract = CloseCommand();
            } else {
                closeContract = Contract.empty();
            }

            closeContract.then(() => {
                // Don't set idle SmartUIFlag if freespins have been triggered but not yet played
                if (this.inFreespins || !this.pendingFreespins) {
                    uiFlags.set(UIFlag.IDLE, true);
                }

                if (this.inFreespins) {
                    resolve(); // AGSFreespinIdleState will handle setIdle and winningsUpdate
                } else {
                    if(gameState.getCurrentGame().isRecovered) {
                        gameWrapper.balancesUpdate(
                            gameState.getCurrentGame().realBalance,
                            gameState.getCurrentGame().freeBalance
                        );
                        gameWrapper.balancesUpdate(
                            gameState.getCurrentGame().realBalance,
                            gameState.getCurrentGame().freeBalance
                        );
                    }

                    if(this.cashWon > 0) {
                        gameWrapper.winningsUpdate(this.cashWon);
                    }

                    wrapperImplementation.onResume.addOnce(() => resolve());
                    gameWrapper.setIdle();
                }
            })
        });
    }
}
